import {LDProvider} from 'launchdarkly-react-client-sdk';
import React, {useEffect, useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {getLaunchDarklyContext} from 'launchdarkly/LaunchDarklyUtil';
import {isNodeTesting} from 'lib/Browser';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

/**
 * A light wrapper around LaunchDarkly's own {@link LDProvider} that checks whether we're in a unit testing
 * context before attempting to wrap the children with {@link LDProvider}.
 *
 * LaunchDarkly clients must be singletons, so in App.initialize(), we create a client and attach it to
 * `window` for React to grab and pass into the provider. (Otherwise, the provider would instantiate one
 * client per React root.
 */
export default function LaunchDarklyProvider({children}: {children: JSX.Element}): JSX.Element {
  const shouldWrapWithLDContext = window.ldClient != null && !isNodeTesting;
  const context = useMemo(() => {
    if (shouldWrapWithLDContext) {
      return getLaunchDarklyContext();
    }
    return null;
  }, [shouldWrapWithLDContext]);
  const [isLDClientReady, setIsLDClientReady] = useStateIfMounted(
    () => shouldWrapWithLDContext && window.ldClient?.ready === true
  );
  useEffect(() => {
    if (shouldWrapWithLDContext && !window.ldClient!.ready) {
      window.ldClient!.waitUntilReady().then(() => {
        window.ldClient!.ready = true;
        setIsLDClientReady(true);
      });
    }
  }, [shouldWrapWithLDContext, setIsLDClientReady]);
  if (shouldWrapWithLDContext && context != null && isLDClientReady) {
    return (
      <LDProvider
        clientSideID={BenchlingConfig.LAUNCHDARKLY_CLIENT_ID}
        context={context}
        ldClient={window.ldClient}
      >
        {children}
      </LDProvider>
    );
  } else {
    return children;
  }
}
